import {
  formatNumber,
  roundToNearestIncrement,
} from '../../../../helpers/formatting';
import { findAttributeValues } from '../../../../helpers/findAttributeValues';
import { Currency } from '../../../../types';
import type { SectionView, IAdAttributeView } from '../../../../types';
import type {
  FinanceInfo,
  DealerSummary,
} from '../../../../components/PageWrappers/AdDetails/types';

import { truncateSearchTerms } from '../../../../helpers/formatSearchTerms';
import getConfig from 'next/config';

export const generateSaveSearchModalData = (
  displayAttributes: IAdAttributeView[],
  price: string | null,
  currency: Currency,
  section?: SectionView,
  county?: string,
  wanted?: boolean,
) => {
  const { make, model, year, transmission, bodyType } = findAttributeValues(
    displayAttributes,
    ['make', 'model', 'year', 'transmission', 'bodyType'],
  );
  const priceAsNumber = price ? parseInt(price.replace(/,/g, '')) : 0;
  const chosenCurrency = currency === 'GBP' ? '£' : '€';

  const name = `${make ?? 'unknown make'} ${model ?? 'unknown model'} in ${
    section?.displayName ?? 'unknown section'
  }`;
  const formattedPrice =
    priceAsNumber > 1
      ? `${chosenCurrency}${formatNumber(
          roundToNearestIncrement(priceAsNumber * 0.85, 100),
        )} - ${chosenCurrency}${formatNumber(
          roundToNearestIncrement(priceAsNumber * 1.15, 100),
        )}`
      : undefined;
  const formattedYear = year
    ? `${parseInt(year) - 1} - ${parseInt(year) + 1}`
    : undefined;
  const formattedCounty = county
    ? `${county.replace('Co. ', '')} (All Areas)`
    : undefined;
  const formattedAdType = wanted ? 'Wanted' : undefined;
  const formattedMakeModel = make && model ? `${make} ${model}` : undefined;
  const vehicleAttributes = [
    section?.displayName,
    formattedMakeModel,
    formattedYear,
    formattedPrice,
    formattedAdType,
    formattedCounty,
    transmission,
    bodyType,
  ];
  const terms = vehicleAttributes.filter(
    (attribute): attribute is string => !!attribute,
  );
  const truncatedTerms = truncateSearchTerms(terms, 5);

  return {
    name,
    terms: truncatedTerms,
  };
};

interface FinanceInfoLink
  extends Omit<FinanceInfo, 'partnerName' | 'partnerType'> {}

export const generateFinanceLink = (financeInfo: FinanceInfoLink) => {
  const {
    publicRuntimeConfig: { BASE_URL },
  } = getConfig();
  return financeInfo?.internalRedirectURL
    ? `${BASE_URL}${financeInfo?.internalRedirectURL}`
    : financeInfo?.externalRedirectURL
    ? financeInfo?.externalRedirectURL
    : '';
};

export const getMileageValueFromString = (mileage: string) =>
  mileage.replace(/\D/g, '');
export const getMileageUnitFromString = (mileage: string) =>
  mileage.includes('mi') ? 'MILES' : 'KILOMETERS';

export const getSellerPhoto = (
  isDealer: boolean,
  dealer?: DealerSummary,
): string => {
  const {
    publicRuntimeConfig: { CDN_STATIC_ASSETS },
  } = getConfig();

  if (isDealer && dealer?.logo?.small) {
    return dealer.logo.small;
  }

  return `${CDN_STATIC_ASSETS}/images/illustrations/profile-new.svg`;
};
